import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'chatOptionsForConversationButtonLabel'
	| 'chatPinLabel'
	| 'chatUnpinLabel'
	| 'chatRenameLabel'
	| 'chatDeleteLabel'
	| 'lastMessageTimestampWithSeparator'
	| 'footerSeparator'
	| 'scopesOverflowCount'
	| 'scopesOverflowAccessibleLabel'
	| 'imagePreviewTitle'
	| 'filePreviewFallbackDescription';

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		chatOptionsForConversationButtonLabel: {
			id: 'ai-mate.all-chat-view.chat-item.chat-options-button-label',
			defaultMessage: 'Chat options for {conversationName}',
			description:
				'Accessibility label (aria-label) for a button or menu trigger in a chat conversation list item within the AI conversation interface that opens options for a specific conversation. Shown as a screen reader announcement when the button is focused or hovered. The conversationName placeholder will be substituted with the name of the conversation (e.g., "Project Planning Discussion"). The purpose is to provide accessible information about which conversation\'s options will be opened for users with assistive technologies.',
		},
		chatPinLabel: {
			id: 'ai-mate.all-chat-view.chat-item.pin-label',
			defaultMessage: 'Pin conversation',
			description:
				'Button or menu item label for pinning a chat conversation so it stays above regular recent conversations.',
		},
		chatUnpinLabel: {
			id: 'ai-mate.all-chat-view.chat-item.unpin-label',
			defaultMessage: 'Unpin conversation',
			description:
				'Button or menu item label for unpinning a chat conversation so it returns to normal recency ordering.',
		},
		chatRenameLabel: {
			id: 'ai-mate.all-chat-view.chat-item.rename-label',
			defaultMessage: 'Rename',
			description:
				'Menu item label displayed in a dropdown menu in a chat conversation list item within the AI conversation interface. Shown as clickable text in a menu. When clicked, it opens a dialog or input field allowing users to rename the conversation. The purpose is to provide users with a way to change the name of a conversation for better organization.',
		},
		chatDeleteLabel: {
			id: 'ai-mate.all-chat-view.chat-item.delete-label',
			defaultMessage: 'Delete',
			description:
				'Menu item label displayed in a dropdown menu in a chat conversation list item within the AI conversation interface. Shown as clickable text in a menu. When clicked, it opens a confirmation dialog and then permanently deletes the conversation. The purpose is to provide users with a way to remove conversations they no longer need.',
		},
		lastMessageTimestampWithSeparator: {
			id: 'ai-mate.all-chat-view.chat-item.last-message-timestamp-with-separator',
			defaultMessage: '\u00B7 {lastMessageTimeDelta}',
			description:
				'Timestamp text displayed in the footer of a chat conversation list item, prefixed with a middle dot separator. Shown when an agent name precedes it. For example: "\u00B7 2 hours ago".',
		},
		footerSeparator: {
			id: 'ai-mate.all-chat-view.chat-item.footer-separator',
			defaultMessage: '\u00B7',
			description:
				'Middle dot separator used between footer items in a chat conversation list item. For example between agent name, scopes, and timestamp.',
		},
		scopesOverflowCount: {
			id: 'ai-mate.all-chat-view.chat-item.scopes-overflow-count',
			defaultMessage: '+{count}',
			description:
				'Text showing the number of additional scopes that are not individually displayed in the footer of a chat conversation list item. For example, if 3 of 4 scopes are shown, this displays "+1".',
		},
		scopesOverflowAccessibleLabel: {
			id: 'conversation-assistant-ui-components.common.chat-conversation-list-refresh.chat-item.scopesOverflowAccessibleLabel',
			defaultMessage: '{count, plural, one {# more source} other {# more sources}}',
			description:
				'Accessible text that explains how many additional conversation sources are not individually displayed.',
		},
		imagePreviewTitle: {
			id: 'ai-mate.all-chat-view.chat-item.image-preview-title',
			defaultMessage: 'Image Preview',
			description:
				'Title displayed in the modal header when previewing an image from a chat conversation list item.',
		},
		filePreviewFallbackDescription: {
			id: 'rovo.chat-list.file-preview.fallback-description',
			defaultMessage: 'Preview not available for this file type.',
			description:
				'Message displayed in the file preview modal when the file type cannot be previewed inline.',
		},
	});

export default message;
